import * as React from 'react';

interface UseStickyTableHeaderOptions {
  wrapperRef: React.RefObject<HTMLDivElement>;
  tableRef: React.RefObject<HTMLTableElement>;
  enabled: boolean;
  dependencies: any[];
}

export function useStickyTableHeader({
  wrapperRef,
  tableRef,
  enabled,
  dependencies = [],
}: UseStickyTableHeaderOptions) {
  React.useEffect(() => {
    if (!enabled) return;

    const wrapper = wrapperRef.current;
    const table = tableRef.current;
    if (!wrapper || !table) return;

    const thead = table.querySelector('thead');
    if (!thead) return;

    // Create sticky header elements
    const container = document.createElement('div');
    const stickyTable = document.createElement('table');
    const clonedThead = document.createElement('thead');

    stickyTable.className = table.className;
    stickyTable.style.borderTop = 'none';
    stickyTable.style.borderBottom = 'none';
    stickyTable.classList.add('sticky-table-header__table');
    stickyTable.appendChild(clonedThead);

    container.className = 'sticky-table-header';
    container.setAttribute('aria-hidden', 'true');
    container.style.position = 'fixed';
    container.style.top = '0px';
    container.style.overflow = 'hidden';
    container.style.zIndex = '999';
    container.style.display = 'none';
    container.appendChild(stickyTable);

    table.insertAdjacentElement('afterend', container);

    // Sync the header rows
    const syncClonedThead = () => {
      clonedThead.innerHTML = '';
      const originalHeaderRow = thead.querySelector('tr');
      if (!originalHeaderRow) return;
      const clonedRow = originalHeaderRow.cloneNode(true) as HTMLTableRowElement;
      clonedThead.appendChild(clonedRow);
    };

    const updateStickyStyles = () => {
      const wrapperRect = wrapper.getBoundingClientRect();
      const tableRect = table.getBoundingClientRect();

      const isVisible = wrapperRect.top < 0 && tableRect.bottom > 0;
      if (!isVisible) {
        container.style.display = 'none';
        return;
      }

      container.style.display = 'block';
      container.style.top = '0px';
      container.style.left = `${wrapperRect.left}px`;
      container.style.width = `${wrapperRect.width}px`;
      stickyTable.style.transform = `translateX(-${wrapper.scrollLeft}px)`;
      stickyTable.style.width = `${table.offsetWidth}px`;

      const originalThs = table.querySelectorAll('thead th');
      const clonedThs = clonedThead.querySelectorAll('th');

      clonedThs.forEach((th, i) => {
        const originalTh = originalThs[i] as HTMLElement;
        const clonedTh = th as HTMLElement;
        if (originalTh && clonedTh) {
          clonedTh.style.width = `${originalTh.offsetWidth}px`;
          clonedTh.style.boxSizing = 'border-box';
          clonedTh.style.cssText += originalTh.style.cssText;
        }
      });
    };

    const resizeObserver = new ResizeObserver(updateStickyStyles);
    resizeObserver.observe(table);
    resizeObserver.observe(wrapper);

    window.addEventListener('scroll', updateStickyStyles);
    window.addEventListener('resize', updateStickyStyles);
    wrapper.addEventListener('scroll', updateStickyStyles);

    // Initial render
    syncClonedThead();
    updateStickyStyles();

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('scroll', updateStickyStyles);
      window.removeEventListener('resize', updateStickyStyles);
      wrapper.removeEventListener('scroll', updateStickyStyles);
      container.remove();
    };
  }, [enabled, wrapperRef, tableRef, ...dependencies]);
}

export default useStickyTableHeader;
